import React from 'react';
import { Section, Title, Entry, List } from '../Section';
import styles from '../Resume.module.scss';

export const Experience = ({ ...props }) => {
    return (
        <Section {...props}>
            <Title id='experience'>Experience</Title>

            <ExperienceEntry
                firm="Sainsbury's"
                position='Senior Frontend Engineer'
                consultancy='via Cressoft Consultancy'
                date='Mar 2025 – Jul 2026'
                achievements={sainsburysAchievements}
                technologies={sainsburysTechnologies}
            />

            <ExperienceEntry
                firm='Stenn International'
                position='Frontend Engineer'
                date='Oct 2023 – Dec 2024'
                achievements={stennAchievements}
                technologies={stennTechnologies}
            />

            <ExperienceEntry
                firm='Builder.ai'
                position='Frontend Engineer'
                consultancy='via Cressoft Consultancy'
                date='Jul 2022 – Oct 2023'
                achievements={builderAiAchievements}
                technologies={builderAiTechnologies}
            />

            <ExperienceEntry
                firm='Ceiba Healthcare'
                position='Frontend Engineer'
                date='May 2021 – Jul 2022'
                achievements={ceibaAchievements}
                technologies={ceibaTechnologies}
            />
            <ExperienceEntry
                firm='Evet Technologies'
                position='Frontend Engineer'
                date='Mar 2020 – Apr 2021'
                achievements={evetAchievements}
                technologies={evetTechnologies}
            />
        </Section>
    );
};

const ExperienceEntry = ({ firm, position, consultancy, date, achievements, technologies }) => {
    return (
        <Entry>
            <div className={styles.sectionEntryHeader}>
                <div className={styles.positionInformation}>
                    <span>{firm}</span>
                    <span>, {position}</span>
                    {consultancy && <span className={styles.consultancyInformation}>{consultancy}</span>}
                </div>
                {date && <div className={styles.sectionEntryHeaderDate}>{date}</div>}
            </div>
            <List className={styles.achievementList} smallTextOnPrint smallText indent list={achievements} numberOfCols='1' />
            <div className={styles.technologyBlock} role='group' aria-label={`Technologies used at ${firm}`}>
                <List className={styles.technologyList} compact list={technologies} numberOfCols='3' />
                <p className={styles.technologyLine}>
                    <strong>Tech stack:</strong> {technologies.join(', ')}
                </p>
            </div>
        </Entry>
    );
};

const sainsburysAchievements = [
    'Modernized the end-to-end Argos Pay@Browse kiosk journey from product discovery and comparison through basket, checkout, payment, loyalty, and order confirmation using React and Electron on Windows app and browser.',
    'Replaced Webpack with Vite, cutting cold-start-to-first-render time from approximately 120 seconds to 10 seconds.',
    'Reworked GitHub Actions and Cypress diagnostics, cutting pipeline runtime from an inconsistent 15–20 minutes to a reliable 7 minutes while surfacing slow tests, requests, and previously hidden failures.',
    'Centralized product, basket, location, quantity, and availability state with React Context and React Query, reducing duplicate API calls and simplifying mixed fulfilment, stock, and pricing logic.',
    'Designed reusable, typed components and hooks for multi-item baskets, stock availability, gift-card and split payments, and QR, swipe, and manual-entry loyalty journeys.'
];

const sainsburysTechnologies = [
    'React',
    'TypeScript',
    'Vite',
    'Electron',
    'React Query',
    'React Context',
    'GitHub Actions',
    'Cypress',
    'styled-components',
    'Jest',
    'React Testing Library',
    'MSW'
];

const stennAchievements = [
    'Created PRDs and translated business requirements into frontend architecture and led complex features through delivery, coordinating API, backend, and cross-team dependencies.',
    'Owned the design and implementation of core customer journeys, including application and reapplication flows, the customer wallet, and broader UX improvements.',
    'Automated TypeScript type generation from the GraphQL schema, strengthening type safety and reducing manual data-model maintenance.',
    'Expanded the shared Material UI component library to standardize reusable UI across product teams.',
    'Owned frontend error monitoring and incident alerting with Sentry and Opsgenie, improving visibility into production issues.'
];

const stennTechnologies = [
    'React',
    'TypeScript',
    'GraphQL',
    'React Query',
    'Material UI',
    'Azure',
    'Sentry',
    'Opsgenie',
    'Jest',
    'Storybook',
    'React Email'
];

const builderAiAchievements = [
    'Delivered reusable, block-based React and TypeScript pages across e-commerce, e-learning, and core product teams on three-month project timelines.',
    'Optimized component architecture and performance while building reusable components with TypeScript and Material UI.',
    'Raised code quality through peer reviews, refactoring guidance, and hands-on support for other developers.',
    'Built unit and integration tests to maintain at least 80% coverage in every file across the application.'
];

const builderAiTechnologies = ['React', 'TypeScript', 'Material UI', 'Jest', 'SonarQube', 'HTML/CSS'];

const ceibaAchievements = [
    'Reduced a data-intensive page from more than 4,000 lines of code to approximately 1,000 lines and cut render time from 600 ms to under 100 ms by redesigning caching and visualization logic.',
    'Built a reusable component system—including form controls, a dynamic data grid, filters, calendars, lazy loading, and virtualization—to improve consistency, customization, and delivery speed.',
    'Introduced MobX global state management and separated models, API services, views, and configuration, improving maintainability and alignment with SOLID principles.',
    'Refactored three of the application’s four most heavily used critical pages while guiding other developers through the new architecture and implementation.'
];

const ceibaTechnologies = ['React', 'JavaScript', 'MobX', 'ChartJS', 'HTML/CSS', 'i18n'];

const evetAchievements = [
    'Delivered features across four concurrent React and MobX products in the affiliate marketing industry.',
    'Built a shared internal component library, documented in Storybook and covered by Mocha and Cypress tests, improving reuse and consistency across products.'
];

const evetTechnologies = ['React', 'MobX', 'Gatsby', 'Storybook', 'Cypress', 'Mocha', 'HTML/CSS', 'i18n'];
